import { useContext, useEffect } from "react";
import useSWR from "swr";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import CustomModal from "../components/modal";
import { hrGetWaitingListDetailsEndpoint } from "../hr_endpoints";
import { ToastDataContext } from "../context/ToastContextProvider";

// The same view the admin panel has, for the lists this HR's companies own.
// The endpoint scopes that server-side; nothing here is load-bearing for it.
export default function HRWaitingListModal({ open, setOpen, listId, listDate }) {

    const { t } = useTranslation()
    const toastContext = useContext(ToastDataContext)

    const { data, error } = useSWR(
        open && listId ? [hrGetWaitingListDetailsEndpoint(listId)] : null
    )

    useEffect(() => {
        if (error) toastContext.error(error.message)
    }, [error])

    // Withdrawing is the only thing that takes somebody off the list; catching
    // a slot does not, so these two are counted separately rather than one
    // being a subset of the other.
    const active = data?.filter((entry) => entry.is_active) || []
    const claimed = active.filter((entry) => entry.has_claimed).length

    return (
        <CustomModal
            open={open}
            setOpen={setOpen}
            title={`${t("dashboard.waitingListTitle")}${listDate ? ` — ${dayjs(listDate).format("DD-MM-YYYY")}` : ""}`}
            confirmMode={true}
            // This modal renders its close label from the prop rather than
            // hardcoding one the way the admin build does, so without it the
            // button comes out blank.
            cancelButton={t("buttons.close")}
            onConfirmModeButton={() => setOpen(false)}
            withIcon={false}
            highWidthTable={true}
        >
            <div className="mb-3 text-sm text-neutral-500">
                {data?.length
                    ? t("dashboard.waitingListSummary", { amount: active.length, claimed })
                    : t("dashboard.waitingListEmpty")}
            </div>
            <div className="overflow-x-auto overflow-y-auto max-h-96">
                <table className="table table-zebra w-full">
                    <thead>
                        <tr>
                            <th>{t("dashboard.waitingListName")}</th>
                            <th>{t("dashboard.waitingListContact")}</th>
                            <th>{t("dashboard.waitingListJoinedAt")}</th>
                            <th>{t("dashboard.waitingListNotifications")}</th>
                            <th>{t("dashboard.waitingListGotSlot")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.map((entry) => (
                            <tr key={entry.id}>
                                <td>
                                    <div className="flex flex-col">
                                        <span>{entry.first_name} {entry.last_name}</span>
                                        {!entry.is_active && (
                                            <span className="text-xs text-neutral-400">
                                                {t("dashboard.waitingListWithdrew")}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="flex flex-col">
                                        <span>{entry.email || ""}</span>
                                        <span className="text-xs text-neutral-400">{entry.phone_number || ""}</span>
                                    </div>
                                </td>
                                <td>{dayjs(entry.joined_at).format("DD-MM-YYYY HH:mm")}</td>
                                <td>
                                    <div className="flex flex-col">
                                        <span>{entry.notified_amount}</span>
                                        {entry.last_notified_at && (
                                            <span className="text-xs text-neutral-400">
                                                {t("dashboard.waitingListLastNotified")}:{" "}
                                                {dayjs(entry.last_notified_at).format("DD-MM-YYYY HH:mm")}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    {entry.has_claimed ? (
                                        <div className="flex flex-col">
                                            <span className="text-accent_green font-semibold">
                                                {t("dashboard.waitingListYes")}
                                            </span>
                                            <span className="text-xs text-neutral-400">
                                                {/* claimed_at is null when the slot was assigned
                                                    rather than claimed by the person themselves. */}
                                                {entry.slot_time || ""}
                                                {entry.claimed_at
                                                    ? ` · ${dayjs(entry.claimed_at).format("DD-MM-YYYY HH:mm")}`
                                                    : ` · ${t("dashboard.waitingListAssigned")}`}
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="text-tertiary font-semibold">
                                            {t("dashboard.waitingListNo")}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </CustomModal>
    )
}
